import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../supabaseClient";
import { cancelSecuredSignup, fetchMySignups } from "../../utility/submitSignup";
import {
  canRequestRefundEntry,
  canSelfServeCancelEntry,
  requiredCancelCompanionNote,
  requiredCancelConfirmCopy,
  requiredCancelNeedsCardRefund,
  requiredRefundRequestCopy,
} from "../../utility/signupAvailability";
import { tournamentSignupToCartLine } from "../../utility/signupCart";
import { isDueCartLine } from "../../utility/signupPaymentQuote";
import { requestSignupRefund } from "../../utility/signupPayments";
import { refreshDueSignupCount } from "../../hooks/useDueSignupCount";
import PayNowPanel from "../../components/Signup/PayNowPanel";
import {
  AlertBox,
  Card,
  ConfirmDialog,
} from "../../components/UI/DesignStandards";
import {
  DashboardHeader,
  DashboardTabs,
  EmptyState,
  LoadingSkeleton,
  SearchAndFilters,
  SignupCard,
} from "./components";
import {
  filterMySignups,
  getUniqueEvents,
  getUniqueSquads,
  getUniqueTournaments,
  mySignupTabCounts,
  resolveMySignupTab,
  signupTab,
  signupsForTab,
  sortMySignups,
} from "./utils/filterAndSortUtils";
import { tournamentPath } from "../../utility/tournamentUrls";

const dueGroupsFromSignups = (signups = []) => {
  const groups = [];
  const byId = new Map();
  signups.forEach((signup) => {
    if (signupTab(signup) !== "due") return;
    const line = tournamentSignupToCartLine(signup);
    if (!isDueCartLine(line) || !line.tournamentId) return;
    if (!byId.has(line.tournamentId)) {
      const group = {
        id: line.tournamentId,
        name: signup.tournament?.name || "Tournament",
        lines: [],
      };
      byId.set(line.tournamentId, group);
      groups.push(group);
    }
    byId.get(line.tournamentId).lines.push(line);
  });
  return groups;
};

const UserSignupDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payResult, setPayResult] = useState(null);
  const [cancelSignup, setCancelSignup] = useState(null);
  const [refundSignup, setRefundSignup] = useState(null);
  const [activeTab, setActiveTab] = useState(() =>
    resolveMySignupTab(searchParams.get("tab"), 1)
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [tournamentFilter, setTournamentFilter] = useState("");
  const [eventFilter, setEventFilter] = useState("");
  const [squadFilter, setSquadFilter] = useState("");

  useEffect(() => {
    if (!user) {
      navigate("/signin");
      return;
    }
    fetchUserSignups();
  }, [user, navigate]);

  const fetchUserSignups = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchMySignups();
      const { data: requests } = await supabase
        .from("signupRefundRequests")
        .select("signupID")
        .eq("status", "open");
      const requested = new Set((requests || []).map((row) => row.signupID));
      const withRequests = (rows) =>
        (rows || []).map((signup) => ({
          ...signup,
          refundRequested: requested.has(signup.id),
        }));
      setSignups(withRequests(data.signups || data.activeSignups || []));
    } catch (err) {
      setError(err.message || "Could not load your signups.");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestRefund = async () => {
    if (!refundSignup) return;
    try {
      await requestSignupRefund({ signupId: refundSignup.id });
      await fetchUserSignups();
      setRefundSignup(null);
    } catch (err) {
      setError(err.message || "Could not request a refund.");
      setRefundSignup(null);
    }
  };

  const handleViewSignup = (signup) => {
    navigate(
      tournamentPath(
        signup.tournament?.slug || signup.tournament?.id,
        `/signups/edit/${signup.id}`
      )
    );
  };

  const handleCancelSignup = async () => {
    if (!cancelSignup) return;
    try {
      await cancelSecuredSignup({
        signupId: cancelSignup.id,
        paymentStatus: cancelSignup.paymentStatus,
        cardRefund: requiredCancelNeedsCardRefund(cancelSignup, signups),
      });
      await fetchUserSignups();
      refreshDueSignupCount();
      setCancelSignup(null);
    } catch (err) {
      setError(err.message || "Could not cancel this signup.");
      setCancelSignup(null);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setTournamentFilter("");
    setEventFilter("");
    setSquadFilter("");
  };

  const lifetimeCounts = useMemo(
    () => mySignupTabCounts(signups),
    [signups]
  );
  const tournamentScoped = useMemo(
    () =>
      tournamentFilter
        ? signups.filter((signup) => signup.tournament?.id === tournamentFilter)
        : signups,
    [signups, tournamentFilter]
  );
  const availableTournaments = useMemo(
    () => getUniqueTournaments(signups),
    [signups]
  );
  const availableEvents = useMemo(
    () => getUniqueEvents(tournamentScoped),
    [tournamentScoped]
  );
  const availableSquads = useMemo(
    () =>
      getUniqueSquads(
        eventFilter
          ? tournamentScoped.filter(
              (signup) => signup.tournamentEvents?.eventName === eventFilter
            )
          : tournamentScoped
      ),
    [tournamentScoped, eventFilter]
  );
  const filtered = useMemo(
    () =>
      filterMySignups(signups, {
        query: searchQuery,
        tournamentId: tournamentFilter,
        eventName: eventFilter,
        squadId: squadFilter,
      }),
    [signups, searchQuery, tournamentFilter, eventFilter, squadFilter]
  );
  const tabCounts = useMemo(() => mySignupTabCounts(filtered), [filtered]);
  const visibleSignups = useMemo(
    () => sortMySignups(signupsForTab(filtered, activeTab), activeTab),
    [filtered, activeTab]
  );
  const dueGroups = useMemo(
    () => dueGroupsFromSignups(visibleSignups),
    [visibleSignups]
  );
  const hasDuePay =
    dueGroups.length > 0 &&
    (activeTab === "due" || activeTab === "all");
  const hasFilters = Boolean(
    searchQuery || tournamentFilter || eventFilter || squadFilter
  );

  useEffect(() => {
    const next = resolveMySignupTab(activeTab, lifetimeCounts.due);
    if (next !== activeTab) setActiveTab(next);
  }, [activeTab, lifetimeCounts.due]);

  const selectTab = (tab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    setSearchParams(next, { replace: true });
  };

  const otherTabHint = useMemo(() => {
    if (visibleSignups.length > 0 || !hasFilters) return "";
    const hits = [
      tabCounts.due && `${tabCounts.due} on Due`,
      tabCounts.upcoming && `${tabCounts.upcoming} on Upcoming`,
      tabCounts.past && `${tabCounts.past} on Past`,
      tabCounts.cancelled && `${tabCounts.cancelled} on Cancelled`,
    ].filter(Boolean);
    if (!hits.length) return "";
    return `Matches in other tabs: ${hits.join(", ")}.`;
  }, [visibleSignups.length, hasFilters, tabCounts]);

  const handlePaid = async (status) => {
    await fetchUserSignups();
    refreshDueSignupCount();
    if (status === "pending") {
      setPayResult("pending");
      return;
    }
    setPayResult(null);
    selectTab("upcoming");
  };

  if (loading) {
    return <LoadingSkeleton />;
  }

  const cancelCopy = cancelSignup
    ? requiredCancelConfirmCopy(cancelSignup, signups)
    : null;
  const refundCopy = refundSignup
    ? requiredRefundRequestCopy(refundSignup, signups)
    : null;

  const listCard = (
    <div className="card-board overflow-hidden">
      {visibleSignups.length === 0 ? (
        <EmptyState
          activeTab={activeTab}
          hasActiveFilters={hasFilters}
          onClearFilters={handleClearFilters}
          otherTabHint={otherTabHint}
        />
      ) : (
        <ul className="divide-y divide-brand-charcoal/10">
          {visibleSignups.map((signup) => (
            <SignupCard
              key={signup.id}
              signup={signup}
              onViewSignup={handleViewSignup}
              onCancelSignup={setCancelSignup}
              onRequestRefund={setRefundSignup}
              showCancel={canSelfServeCancelEntry(signup, signups)}
              showRequestRefund={canRequestRefundEntry(signup, signups)}
              companionNote={requiredCancelCompanionNote(signup, signups)}
            />
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-brand-cream">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {payResult === "pending" ? (
          <AlertBox
            type="warning"
            title="Payment is still processing."
            message="If status still says Due, wait a moment and refresh."
          />
        ) : null}

        {error ? (
          <AlertBox
            type="error"
            title="Please fix this"
            message={error}
          />
        ) : null}

        <DashboardHeader>
          {signups.length > 0 ? (
            <>
              <DashboardTabs
                activeTab={activeTab}
                onTabChange={selectTab}
                counts={tabCounts}
                showDue={lifetimeCounts.due > 0}
              />
              <SearchAndFilters
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                tournamentFilter={tournamentFilter}
                onTournamentFilterChange={(value) => {
                  setTournamentFilter(value);
                  setEventFilter("");
                  setSquadFilter("");
                }}
                eventFilter={eventFilter}
                onEventFilterChange={(value) => {
                  setEventFilter(value);
                  setSquadFilter("");
                }}
                squadFilter={squadFilter}
                onSquadFilterChange={setSquadFilter}
                availableTournaments={availableTournaments}
                availableEvents={availableEvents}
                availableSquads={availableSquads}
                showTournamentNameOnSquads={
                  !tournamentFilter && availableTournaments.length > 1
                }
                onClearFilters={handleClearFilters}
              />
            </>
          ) : null}
        </DashboardHeader>

        {signups.length === 0 ? (
          <div className="card-board overflow-hidden">
            <div className="p-6">
              <p className="text-brand-charcoal">Nothing here yet.</p>
              <p className="text-sm text-brand-muted mt-2">
                <Link
                  className="underline text-brand-gold-ink"
                  to="/tournaments"
                >
                  Find tournaments
                </Link>{" "}
                to add an event.
              </p>
            </div>
          </div>
        ) : hasDuePay ? (
          <div className="lg:grid lg:grid-cols-3 lg:gap-8 lg:items-start">
            <aside className="order-last lg:order-none lg:col-start-3 lg:row-start-1 mt-6 lg:mt-0 lg:sticky lg:top-3 space-y-6">
              {dueGroups.map((group) => (
                <Card
                  key={group.id}
                  className="overflow-hidden lg:max-h-[calc(100vh-1.5rem)] lg:overflow-y-auto"
                  padding=""
                >
                  <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
                    <h2 className="font-display font-bold text-lg text-brand-cream tracking-tight">
                      Pay now
                    </h2>
                    <p className="text-sm text-brand-mist mt-1">
                      {group.name}
                    </p>
                    <p className="text-sm text-brand-mist">
                      {group.lines.length} due
                    </p>
                  </div>
                  <div className="p-6">
                    <PayNowPanel
                      tournamentId={group.id}
                      lines={group.lines}
                      user={user}
                      onPaid={handlePaid}
                      hideTitle
                    />
                  </div>
                </Card>
              ))}
            </aside>
            <div className="lg:col-span-2 lg:col-start-1 lg:row-start-1">
              {listCard}
            </div>
          </div>
        ) : (
          listCard
        )}

        {cancelSignup && cancelCopy ? (
          <ConfirmDialog
            isOpen={!!cancelSignup}
            onClose={() => setCancelSignup(null)}
            onConfirm={handleCancelSignup}
            title={cancelCopy.title}
            message={cancelCopy.message}
            confirmText={cancelCopy.confirmText}
            cancelText="Keep signup"
            variant="danger"
          />
        ) : null}
        {refundSignup && refundCopy ? (
          <ConfirmDialog
            isOpen={!!refundSignup}
            onClose={() => setRefundSignup(null)}
            onConfirm={handleRequestRefund}
            title={refundCopy.title}
            message={refundCopy.message}
            confirmText={refundCopy.confirmText}
            cancelText="Keep signup"
          />
        ) : null}
      </div>
    </div>
  );
};

export default UserSignupDashboard;
